// src/services/subcontractorService.js
//
// Subcontractor Cost Tracking — governed service layer.
//
// WHY THIS EXISTS (context from this session's discussion):
// Most on-site work is done by sub-contractors, not company staff. The
// Project Manager takes updates from sub-contractors (phone or site visit)
// and this is the real "labour cost" driver for this business — there is
// no separate company-staff DPR/attendance system, and none is planned.
//
// ARCHITECTURE (3 collections, same governed-service-layer discipline as
// every other domain in this system — see ERP_CONSTITUTION Section 10):
//
// 1. subcontractors            — master list (name, trade, phone). Created
//                                 once, reused across projects — same
//                                 pattern as Client Master / Vendor Master.
// 2. subcontractor_assignments — WHICH subcontractor is working on WHICH
//                                 project, at WHAT rate. Rate lives here
//                                 (not on the subcontractor master) because
//                                 rate varies project-to-project, and a
//                                 rate change must never retroactively
//                                 alter a past work log's already-recorded
//                                 amount (Golden Rule — transactions never
//                                 edit).
// 3. subcontractor_work_logs   — one entry per field-visit (logged by a
//                                 site_engineer or technician who actually
//                                 saw the work — the contractor never uses
//                                 this app, so this IS the ground-truth
//                                 observation, not a relayed claim).
//                                 TWO-STAGE FLOW (added 16 Sep 2026, per
//                                 client requirement — "no payment for
//                                 work that isn't verified complete"):
//                                   Stage 1 — logSubcontractorWork() below
//                                     creates the log at status
//                                     "pending_manager_confirmation". NO
//                                     financial_transactions entry yet —
//                                     a field-role logging work is an
//                                     OBSERVATION, not a financial
//                                     authorization.
//                                   Stage 2 — confirmSubcontractorWork()
//                                     (sm/owner/manager ONLY) is the sole
//                                     point that creates the
//                                     financial_transactions entry
//                                     (category: "subcontractor",
//                                     status: "unpaid" — the bill is now
//                                     officially owed, distinct from
//                                     actually being paid, mirroring the
//                                     Vendor Payment pattern elsewhere in
//                                     this app). Log status becomes
//                                     "bill_confirmed_unpaid".
//                                     rejectSubcontractorWork() (same
//                                     role gate) instead sets status
//                                     "rejected" with a mandatory reason —
//                                     no financial_transactions entry is
//                                     ever created for a rejected log.
//
// GOVERNED ENTRY POINT: logSubcontractorWork() is the ONLY function that
// may write to subcontractor_work_logs. No UI may call addDoc on that
// collection directly — mirrors the addRemark()/issueAgainstMR() pattern
// already enforced elsewhere in this codebase.

import { db } from "../firebase";
import {
  collection, addDoc, updateDoc, doc, getDoc, runTransaction,
  getDocs, query, where, serverTimestamp,
} from "firebase/firestore";

// ── SUBCONTRACTOR MASTER ────────────────────────────────────────────────

export async function fetchSubcontractors(company_id) {
  const snap = await getDocs(query(
    collection(db, "subcontractors"),
    where("company_id", "==", company_id)
  ));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(s => s.is_active !== false)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

// Creates a new subcontractor master record. Called inline from the
// Assignment form when the PM types a name not already in the dropdown —
// same "quick-create on the fly" convention already used for clients
// (QuickCreateClient) elsewhere in this app, so the PM never has to leave
// the page to register a new subcontractor.
export async function createSubcontractor({ company_id, name, trade, phone, created_by, created_by_name }) {
  if (!company_id) throw new Error("company_id required");
  const ref = await addDoc(collection(db, "subcontractors"), {
    company_id,
    name: (name || "").trim(),
    trade: (trade || "").trim(),
    phone: (phone || "").trim(),
    is_active: true,
    created_by: created_by || "",
    created_by_name: created_by_name || "",
    created_at: serverTimestamp(),
  });
  return ref.id;
}

// ── ASSIGNMENTS (subcontractor ↔ project, at a rate) ────────────────────

export async function fetchAssignmentsForProject(company_id, project_id) {
  if (!project_id) return [];
  const snap = await getDocs(query(
    collection(db, "subcontractor_assignments"),
    where("company_id", "==", company_id),
    where("project_id", "==", project_id)
  ));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(a => a.status !== "completed");
}

// Marks an assignment as completed so it stops appearing in the "which
// rate to use" picker going forward. Used ONLY for the "rate revision,
// same scope of work" case — the old assignment (and every work log +
// financial_transactions entry already made against it) is left
// completely untouched, just excluded from future selection. This is
// what keeps the assignment picker from growing indefinitely over years
// of routine rate revisions, while genuinely distinct concurrent scopes
// of work for the same subcontractor still show up side by side.
export async function markAssignmentCompleted(assignment_id, company_id) {
  if (!assignment_id) throw new Error("assignment_id required");
  if (!company_id) throw new Error("company_id required");

  const ref  = doc(db, "subcontractor_assignments", assignment_id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Assignment not found");
  if (snap.data().company_id !== company_id) {
    throw new Error("Access denied — assignment belongs to a different company");
  }

  await updateDoc(ref, {
    status: "completed",
  });
}

// billing_mode: "labour" — most common on the ground. Subcontractor charges
//   per labour headcount per category (Skilled/Unskilled/etc.), and the
//   PM only ever enters headcounts + days on each work log — amount is
//   always derived, never manually guessed.
// billing_mode: "lump_sum" — for work genuinely quoted as a flat price
//   (e.g. "₹40,000 for the whole ducting job"), where headcount tracking
//   doesn't apply. Kept as an escape hatch, not the default — most real
//   subcontractor billing in this business is headcount-based.
export async function createAssignment({
  company_id, subcontractor_id, subcontractor_name,
  project_id, project_name, site_id, site_name,
  scope_of_work, billing_mode, labour_rates, lump_sum_rate, work_items,
  created_by, created_by_name,
}) {
  if (!company_id) throw new Error("company_id required");
  const ref = await addDoc(collection(db, "subcontractor_assignments"), {
    company_id,
    subcontractor_id, subcontractor_name,
    project_id, project_name, site_id, site_name,
    scope_of_work: (scope_of_work || "").trim(),
    billing_mode: billing_mode === "lump_sum" ? "lump_sum"
      : billing_mode === "measurement" ? "measurement" : "labour",
    // Rate card — one row per labour category (e.g. Skilled ₹900/day,
    // Unskilled ₹600/day). Not fixed to exactly two categories — PM can
    // add/remove rows freely (e.g. a third "Supervisor" tier), since
    // different trades and contractors split categories differently.
    labour_rates: (labour_rates || [])
      .filter(r => (r.category || "").trim())
      .map(r => ({
        category: r.category.trim(),
        rate_per_day: Number(r.rate_per_day || 0),
      })),
    lump_sum_rate: Number(lump_sum_rate || 0),
    // Trade-wise measurement rate card — one row per trade (e.g. Piping
    // ₹45/Rmt, Ducting ₹60/Sqft). Same "PM adds/removes rows freely"
    // convention as labour_rates above, just keyed by trade+unit instead
    // of labour category — covers subcontractors billed per measured
    // work rather than per headcount.
    work_items: (work_items || [])
      .filter(w => (w.trade || "").trim())
      .map(w => ({
        trade: w.trade.trim(),
        unit: w.unit || "Rmt",
        rate_per_unit: Number(w.rate_per_unit || 0),
      })),
    status: "active",
    created_by: created_by || "",
    created_by_name: created_by_name || "",
    created_at: serverTimestamp(),
  });
  return ref.id;
}

// ── WORK LOGS — GOVERNED ENTRY POINT (Stage 1 — field observation) ──────
// The only function allowed to CREATE subcontractor_work_logs. Does NOT
// write financial_transactions — that only happens at Stage 2, via
// confirmSubcontractorWork() below, after a Manager reviews. No UI may
// ever write either collection directly.
export async function logSubcontractorWork({
  company_id,
  assignment_id, subcontractor_id, subcontractor_name,
  project_id, project_name, site_id, site_name,
  log_date, work_description, billing_mode, days, labour_breakdown,
  measurement_breakdown, amount,
  logged_by, logged_by_name,
}) {
  if (!company_id) throw new Error("company_id required");
  const finalAmount = Number(amount || 0);

  // labour_breakdown: [{category, count, rate_per_day}] — only meaningful
  // when billing_mode is "labour". Zero-count rows are dropped so a log
  // only records categories that actually had people on site that day.
  const cleanBreakdown = (billing_mode === "labour")
    ? (labour_breakdown || [])
        .map(b => ({
          category: b.category,
          count: Number(b.count || 0),
          rate_per_day: Number(b.rate_per_day || 0),
        }))
        .filter(b => b.count > 0)
    : [];

  // measurement_breakdown: [{trade, unit, rate_per_unit, qty}] — only
  // meaningful when billing_mode is "measurement". Zero-qty rows dropped,
  // same convention as labour_breakdown above (a log only records trades
  // that actually had confirmed work this visit).
  const cleanMeasurement = (billing_mode === "measurement")
    ? (measurement_breakdown || [])
        .map(m => ({
          trade: m.trade,
          unit: m.unit,
          rate_per_unit: Number(m.rate_per_unit || 0),
          qty: Number(m.qty || 0),
        }))
        .filter(m => m.qty > 0)
    : [];

  // Stage 1 ONLY — pending_manager_confirmation. No financial_transactions
  // write here (see Stage 2, confirmSubcontractorWork(), below) — a field
  // observation is not yet a financial authorization.
  const logRef = await addDoc(collection(db, "subcontractor_work_logs"), {
    company_id,
    assignment_id, subcontractor_id, subcontractor_name,
    project_id, project_name, site_id, site_name,
    log_date,
    work_description: (work_description || "").trim(),
    billing_mode: billing_mode === "lump_sum" ? "lump_sum"
      : billing_mode === "measurement" ? "measurement" : "labour",
    days: Number(days || 0), // not meaningful for lump_sum/measurement, harmless if 0
    labour_breakdown: cleanBreakdown, // empty array for lump_sum/measurement logs
    measurement_breakdown: cleanMeasurement, // empty array for labour/lump_sum logs
    amount: finalAmount,
    logged_by: logged_by || "",
    logged_by_name: logged_by_name || "",
    status: "pending_manager_confirmation",
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });

  return logRef.id;
}

// ── FETCH LOGS AWAITING MANAGER CONFIRMATION (Stage 2 queue) ────────────
// Company-wide (not project-filtered) — mirrors fetchAllWorkLogs()'s
// scope, since a Manager reviewing this queue typically works across
// multiple projects in one sitting.
export async function fetchPendingConfirmationLogs(company_id) {
  const snap = await getDocs(query(
    collection(db, "subcontractor_work_logs"),
    where("company_id", "==", company_id),
    where("status",     "==", "pending_manager_confirmation")
  ));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.created_at?.seconds || 0) - (b.created_at?.seconds || 0)); // oldest first — first-in-first-reviewed
}

// ── CONFIRM WORK — GOVERNED ENTRY POINT (Stage 2a) ───────────────────────
// sm/owner/manager ONLY (enforced by the caller's role check — see
// SubcontractorWork.jsx). The ONLY function allowed to create a
// financial_transactions entry for subcontractor work.
//
// D-289 Phase 2 (F11, 08 Oct 2026): the bill row and the log's status now
// change in ONE Firestore transaction. Before, the bill row was written first
// and the log updated second — if the second write failed, the log stayed
// "pending_manager_confirmation" and the same work could be confirmed again,
// putting a duplicate bill in the subcontractor's khaata. Now both happen
// together or neither happens, and a second confirm (even at the same moment
// from another screen) sees the new status and is refused.
// The bill row also carries subcontractor_id / subcontractor_name directly
// (older rows don't — khaata.js still resolves those through the work log).
export async function confirmSubcontractorWork({
  log_id, company_id, confirmed_by, confirmed_by_name,
}) {
  if (!log_id)     throw new Error("log_id required");
  if (!company_id) throw new Error("company_id required");

  const logRef = doc(db, "subcontractor_work_logs", log_id);
  // Bill row id reserved up front so it can be written inside the transaction.
  const finRef = doc(collection(db, "financial_transactions"));

  await runTransaction(db, async (tx) => {
    const logSnap = await tx.get(logRef);
    if (!logSnap.exists()) throw new Error("Work log not found");

    const log = logSnap.data();
    if (log.company_id !== company_id) {
      throw new Error("Access denied — this log belongs to a different company");
    }
    if (log.status !== "pending_manager_confirmation") {
      throw new Error(`This log is already ${log.status} — cannot confirm again`);
    }

    const breakdown    = (log.labour_breakdown || []).filter(b => b.count > 0);
    const measurement  = (log.measurement_breakdown || []).filter(m => m.qty > 0);
    const breakdownSuffix = breakdown.length > 0
      ? ` (${breakdown.map(b => `${b.count} ${b.category}`).join(", ")} × ${log.days} day${Number(log.days) === 1 ? "" : "s"})`
      : measurement.length > 0
        ? ` (${measurement.map(m => `${m.qty} ${m.unit} ${m.trade}`).join(", ")})`
        : "";

    tx.set(finRef, {
      company_id,
      type:               "expense",
      category:           "subcontractor",
      amount:             Number(log.amount || 0),
      description:        `Subcontractor: ${log.subcontractor_name} — ${log.work_description || ""}${breakdownSuffix}`,
      reference_type:     "subcontractor_work",
      reference_id:       log_id,
      subcontractor_id:   log.subcontractor_id || "",
      subcontractor_name: log.subcontractor_name || "",
      project_id:         log.project_id || "",
      project_name:       log.project_name || "",
      site_id:            log.site_id || "",
      site_name:          log.site_name || "",
      requested_by:       log.logged_by || "",
      requested_by_name:  log.logged_by_name || "",
      approved_by:        confirmed_by || "",
      approved_by_name:   confirmed_by_name || "",
      status:             "unpaid", // legacy field — paid state comes from the khaata (D-289)
      created_at:         serverTimestamp(),
      updated_at:         serverTimestamp(),
    });

    tx.update(logRef, {
      status:                   "bill_confirmed_unpaid",
      confirmed_by:             confirmed_by || "",
      confirmed_by_name:        confirmed_by_name || "",
      confirmed_at:             serverTimestamp(),
      financial_transaction_id: finRef.id,
      updated_at:               serverTimestamp(),
    });
  });

  return { success: true, financial_transaction_id: finRef.id };
}

// ── REJECT WORK — GOVERNED ENTRY POINT (Stage 2b) ────────────────────────
// sm/owner/manager ONLY. Never creates a financial_transactions entry.
// Reason mandatory — same discipline as every other reject/cancel flow in
// this app. The rejected log stays as a permanent record (Golden Rule); the
// field role logs a fresh entry if the work is re-verified later.
// D-289 Phase 2 (F11): runs in a transaction too, so a confirm and a reject
// clicked at the same moment can never both succeed.
export async function rejectSubcontractorWork({
  log_id, company_id, rejected_by, rejected_by_name, rejection_reason,
}) {
  if (!log_id)     throw new Error("log_id required");
  if (!company_id) throw new Error("company_id required");
  if (!rejection_reason || !rejection_reason.trim()) {
    throw new Error("Rejection reason is mandatory");
  }

  const logRef = doc(db, "subcontractor_work_logs", log_id);

  await runTransaction(db, async (tx) => {
    const logSnap = await tx.get(logRef);
    if (!logSnap.exists()) throw new Error("Work log not found");

    const log = logSnap.data();
    if (log.company_id !== company_id) {
      throw new Error("Access denied — this log belongs to a different company");
    }
    if (log.status !== "pending_manager_confirmation") {
      throw new Error(`This log is already ${log.status} — cannot reject`);
    }

    tx.update(logRef, {
      status:           "rejected",
      rejected_by:      rejected_by || "",
      rejected_by_name: rejected_by_name || "",
      rejection_reason: rejection_reason.trim(),
      rejected_at:      serverTimestamp(),
      updated_at:       serverTimestamp(),
    });
  });

  return { success: true };
}

export async function fetchWorkLogsForProject(company_id, project_id) {
  if (!project_id) return [];
  const snap = await getDocs(query(
    collection(db, "subcontractor_work_logs"),
    where("company_id", "==", company_id),
    where("project_id", "==", project_id)
  ));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
}

// Company-wide list (all projects) — used by the "list" tab. No project
// filter, so this is a broader read than fetchWorkLogsForProject above.
export async function fetchAllWorkLogs(company_id) {
  const snap = await getDocs(query(
    collection(db, "subcontractor_work_logs"),
    where("company_id", "==", company_id)
  ));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
}
